import { ACTIVITY_CATEGORIES, PARTICIPATIONS } from '@yoyoclub/shared';
import imageCompression from 'browser-image-compression';
import { Check, ImageIcon, Link2, Plus, Sparkle, X } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { dateRowToOccurrence, splitByScript, type DateRow } from '../components/picks/pickForm';
import { btnGhost, btnPrimary, focusRing, textLink } from '../components/ui/redesign';
import { useAuth } from '../hooks/useAuth';
import { intakeApi, LOW_CONFIDENCE, type IntakeJob } from '../services/intakeApi';
import { picksApi, type PickSubmissionPayload } from '../services/picksApi';
import { runIntake, settlePendingJob, type IntakeFailure } from '../utils/runIntake';

const REGIONS = ['SOUTH_BAY', 'PENINSULA', 'EAST_BAY', 'SAN_FRANCISCO', 'OTHER'] as const;
const LANGUAGES = ['ZH', 'EN', 'BILINGUAL', 'NA'] as const;
const MIN_REASON = 10;
const MAX_REASON = 500;

const input = `min-h-[44px] w-full rounded-[12px] border-[1.5px] border-line bg-white px-3 py-[11px] text-[15px] outline-none focus:border-primary focus:shadow-[0_0_0_3px_rgba(58,125,68,0.12)]`;
const labelCls = 'flex flex-wrap items-center gap-2 font-ui text-[13px] font-semibold';
/** Highlights from smart intake (prototype `.field.chk` / `.field.miss`). */
type Flag = 'chk' | 'miss';
type FlagKey =
  | 'type'
  | 'title'
  | 'provider'
  | 'url'
  | 'when'
  | 'address'
  | 'city'
  | 'region'
  | 'age'
  | 'participation'
  | 'language'
  | 'category'
  | 'price';
const FLAG_FIELD: Record<Flag, string> = {
  chk: '[&_input]:border-[#B8860B] [&_input]:bg-[#FFF6DA] [&_select]:border-[#B8860B] [&_select]:bg-[#FFF6DA]',
  miss: '[&_input]:border-[#C2410C] [&_input]:bg-[#FFEDE3] [&_select]:border-[#C2410C] [&_select]:bg-[#FFEDE3]',
};
const FLAG_PILL: Record<Flag, string> = {
  chk: 'bg-[#FFF6DA] text-[#8A6508]',
  miss: 'bg-[#FFEDE3] text-[#C2410C]',
};

function FlagPill({ flag, label }: { flag: Flag; label: string }) {
  return (
    <span className={`rounded-full px-2 py-px text-[11.5px] font-semibold ${FLAG_PILL[flag]}`}>
      {label}
    </span>
  );
}

const compressForIntake = (file: File) =>
  imageCompression(file, { maxSizeMB: 4, maxWidthOrHeight: 2560, useWebWorker: true });

const radioCls =
  'inline-flex min-h-[44px] cursor-pointer items-center gap-[7px] rounded-[12px] border-[1.5px] border-line bg-white px-[13px] py-[9px] text-sm font-medium has-[:checked]:border-primary has-[:checked]:bg-primary-pale';

function Field({
  label,
  htmlFor,
  hint,
  full,
  flag,
  children,
}: {
  label: ReactNode;
  htmlFor?: string;
  hint?: ReactNode;
  full?: boolean;
  flag?: Flag;
  children: ReactNode;
}) {
  return (
    <div
      className={`flex min-w-0 flex-col gap-1.5 ${full ? 'desk:col-span-2' : ''} ${flag ? FLAG_FIELD[flag] : ''}`}
    >
      {htmlFor ? (
        <label htmlFor={htmlFor} className={labelCls}>
          {label}
        </label>
      ) : (
        <span className={labelCls}>{label}</span>
      )}
      {children}
      {hint && <span className="text-[12.5px] text-mute">{hint}</span>}
    </div>
  );
}

/** Failures that open the form without a word: nothing the parent can do about them. */
const SILENT_FAILURES = new Set<IntakeFailure>(['quota', 'failed', 'timeout']);

const hostOf = (u: string) => {
  try {
    return new URL(u).hostname.replace(/^www\./, '');
  } catch {
    return u;
  }
};

/**
 * "推荐一个活动" (PRD §6.3, prototype `renderSubmit`). With smart intake on
 * (PICK_INTAKE_ENABLED) it is three steps: a link or an image, then the prefilled form
 * to check, then submit. With it off parents fill the form directly. The reason they
 * write is both their review and the first draft of "why we picked it".
 */
export default function PickSubmit() {
  const { t } = useTranslation(['picks', 'review', 'common']);
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [type, setType] = useState<'PROVIDER' | 'EVENT'>('EVENT');
  const [title, setTitle] = useState('');
  const [provider, setProvider] = useState('');
  const [url, setUrl] = useState('');
  const [dates, setDates] = useState<DateRow[]>([{ date: '', start: '', end: '' }]);
  const [schedule, setSchedule] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [region, setRegion] = useState('');
  const [ageMin, setAgeMin] = useState('');
  const [ageMax, setAgeMax] = useState('');
  const [participation, setParticipation] = useState('PARENT_CHILD');
  const [language, setLanguage] = useState('');
  const [category, setCategory] = useState('');
  const [price, setPrice] = useState('');
  const [reason, setReason] = useState('');
  const [visited, setVisited] = useState(false);
  const [mode, setMode] = useState<'account' | 'custom' | 'anonymous'>('account');
  const [customName, setCustomName] = useState('');
  const [note, setNote] = useState('');
  const [duplicate, setDuplicate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  // Smart intake: null until known; the three-step flow only when it is on.
  const [intakeOn, setIntakeOn] = useState<boolean | null>(null);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [srcMode, setSrcMode] = useState<'url' | 'img'>('url');
  const [srcUrl, setSrcUrl] = useState('');
  const [srcFile, setSrcFile] = useState<File | null>(null);
  const [srcNote, setSrcNote] = useState('');
  const [intakeMsg, setIntakeMsg] = useState<string | null>(null);
  const [recognized, setRecognized] = useState(false);
  // The page would not open: the server searches the web, then says where it read.
  const [searching, setSearching] = useState(false);
  const [searchSources, setSearchSources] = useState<string[] | null>(null);
  const [flags, setFlags] = useState<Partial<Record<FlagKey, Flag>>>({});
  const [draftPickId, setDraftPickId] = useState<string | null>(null);
  const [pendingJobId, setPendingJobId] = useState<string | null>(null);
  const intakeRun = useRef<AbortController | null>(null);

  useEffect(() => {
    intakeApi('parent')
      .enabled()
      .then(setIntakeOn)
      .catch(() => setIntakeOn(false));
    // Leaving the page abandons a running job, so its result is never written later.
    return () => intakeRun.current?.abort();
  }, []);

  const unflag = (key: FlagKey) => setFlags((f) => (f[key] ? { ...f, [key]: undefined } : f));
  const editDates = (fn: (d: DateRow[]) => DateRow[]) => {
    unflag('when');
    setDates(fn);
  };

  /** Prefills from the draft; only fields the draft has, the rest keep what was typed. */
  const applyDraft = (job: IntakeJob) => {
    const d = job.draft;
    const c = job.confidences;
    if (!d || !c) return;
    // Low when any filled part is low: a sure ageMax never hides an unsure ageMin.
    const filled = (k: keyof typeof c) => {
      const v = d[k];
      return v !== null && !(Array.isArray(v) && v.length === 0);
    };
    const low = (...keys: (keyof typeof c)[]) => {
      const parts = keys.filter(filled);
      return parts.length > 0 && Math.min(...parts.map((k) => c[k] ?? 0)) < LOW_CONFIDENCE;
    };
    const next: Partial<Record<FlagKey, Flag>> = {};
    const mark = (key: FlagKey, has: boolean, lowConf: boolean, required = true) => {
      if (!has) {
        if (required) next[key] = 'miss';
      } else if (lowConf) next[key] = 'chk';
    };
    const effType = d.type ?? type;
    if (d.type) setType(d.type);
    // A type is always selected, so a missing or unsure one asks for a check.
    mark('type', true, !d.type || low('type'));
    const titleText = d.titleCN ?? d.titleEN;
    if (titleText) setTitle(titleText);
    mark('title', !!titleText, low('titleCN', 'titleEN'));
    if (d.providerName) setProvider(d.providerName);
    mark('provider', !!d.providerName, low('providerName'));
    if (d.externalUrl) setUrl(d.externalUrl);
    mark('url', !!d.externalUrl, low('externalUrl'));
    if (effType === 'EVENT') {
      if (d.occurrences.length) {
        setDates(d.occurrences.map((o) => ({ date: o.date, start: o.start, end: o.end ?? '' })));
      }
      mark('when', d.occurrences.length > 0, low('occurrences'));
    } else {
      const sched = d.scheduleNoteCN ?? d.scheduleNoteEN;
      if (sched) setSchedule(sched);
      mark('when', !!sched, low('scheduleNoteCN', 'scheduleNoteEN'));
    }
    if (d.addressLine) setAddress(d.addressLine);
    mark('address', !!d.addressLine, low('addressLine'), false);
    if (d.city) setCity(d.city);
    mark('city', !!d.city, low('city'), false);
    if (d.region) setRegion(d.region);
    mark('region', !!d.region, low('region'));
    const hasAge = d.ageMin !== null && d.ageMax !== null;
    if (hasAge) {
      setAgeMin(String(d.ageMin));
      setAgeMax(String(d.ageMax));
    }
    mark('age', hasAge, low('ageMin', 'ageMax'));
    if (d.participation) setParticipation(d.participation);
    // A default is already selected, so a missing value asks for a check, not an entry.
    mark('participation', true, !d.participation || low('participation'));
    if (d.language) setLanguage(d.language);
    mark('language', !!d.language, low('language'));
    if (d.category) setCategory(d.category);
    mark('category', !!d.category, low('category'));
    const priceText = d.priceNoteCN ?? d.priceNoteEN;
    if (priceText) setPrice(priceText);
    mark('price', !!priceText, low('priceNoteCN', 'priceNoteEN'), false);
    setFlags(next);
    // Prefilled values never blur, so run the duplicate hint here (PRD §6.3).
    const dupProvider = d.providerName ?? provider;
    const dupUrl = d.externalUrl ?? url;
    if (dupProvider.trim() || dupUrl.trim()) {
      picksApi
        .checkDuplicate({
          type: effType,
          providerName: dupProvider,
          externalUrl: dupUrl,
          title: titleText ?? title,
        })
        .then((r) => setDuplicate(r.duplicate))
        .catch(() => setDuplicate(false));
    }
  };

  const recognize = async () => {
    setIntakeMsg(null);
    if (srcMode === 'url' ? !srcUrl.trim() : !srcFile) {
      setIntakeMsg(t('picks:intake.needInput'));
      return;
    }
    // An earlier job whose abandon never landed is settled first: if it already wrote
    // a draft, that draft is used, so a new job cannot leave a second one.
    if (pendingJobId) {
      const api = intakeApi('parent');
      try {
        const written = await settlePendingJob(api, pendingJobId);
        setPendingJobId(null);
        if (written) {
          // Keep the draft id first: even if the read below fails, it is reused.
          setDraftPickId(written);
          try {
            const earlier = await api.get(pendingJobId);
            if (earlier.status === 'DONE') {
              applyDraft(earlier);
              setRecognized(true);
              setSearchSources(earlier.viaSearch ? earlier.sources : null);
            }
          } catch {
            setRecognized(false);
          }
          setStep(3);
          return;
        }
      } catch {
        setIntakeMsg(t('picks:intake.errors.network'));
        return;
      }
    }
    intakeRun.current?.abort();
    const run = new AbortController();
    intakeRun.current = run;
    setSearching(false);
    setSearchSources(null);
    setStep(2);
    window.scrollTo({ top: 0 });
    const out = await runIntake(
      srcMode === 'url' ? { url: srcUrl } : { file: srcFile! },
      run.signal,
      {
        api: intakeApi('parent'),
        compress: compressForIntake,
        onSearching: () => setSearching(true),
      }
    );
    if (run.signal.aborted || out.kind === 'cancelled') return;
    if (srcNote.trim()) setReason((r) => (r.trim() ? r : srcNote.trim().slice(0, MAX_REASON)));
    if (out.kind === 'done') {
      applyDraft(out.job);
      setDraftPickId(out.job.draftPickId);
      setRecognized(true);
      if (out.job.viaSearch) setSearchSources(out.job.sources);
    } else {
      // The day's recognitions are used up: later suggestions go straight to the form.
      if (out.reason === 'quota') setIntakeOn(false);
      setFlags({});
      setRecognized(false);
      if (out.draftPickId) setDraftPickId(out.draftPickId);
      if (out.pendingJobId) setPendingJobId(out.pendingJobId);
      // Used-up recognitions and model errors just open the form (PO decision
      // 2026-10-14); only hints the parent can act on are shown.
      setIntakeMsg(SILENT_FAILURES.has(out.reason) ? null : t(`picks:intake.errors.${out.reason}`));
      // Keep what the parent already gave: the pasted page is the sign-up link.
      if (srcMode === 'url') setUrl((u) => u || srcUrl.trim());
    }
    setStep(3);
  };

  // Suggesting needs an account (PRD F1); the login page brings the parent back here.
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate(`/login?redirect=${encodeURIComponent('/picks/submit')}`, { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  const checkDuplicate = () => {
    if (!provider.trim() && !url.trim()) return;
    picksApi
      .checkDuplicate({ type, providerName: provider, externalUrl: url, title })
      .then((r) => setDuplicate(r.duplicate))
      .catch(() => setDuplicate(false));
  };

  const reasonLength = reason.trim().length;
  const occurrences = dates.map(dateRowToOccurrence);

  const submit = async () => {
    setError(null);
    if (reasonLength < MIN_REASON || reasonLength > MAX_REASON) {
      setError(t('picks:submit.reasonRequired'));
      return;
    }
    if (type === 'EVENT' && occurrences.some((o) => o === null)) {
      setError(t('picks:submit.datesIncomplete'));
      return;
    }
    const titleCols = splitByScript(title);
    const priceCols = splitByScript(price);
    const scheduleCols = splitByScript(schedule);
    const cityCols = splitByScript(city);
    const payload: PickSubmissionPayload = {
      type,
      titleCN: titleCols.cn,
      titleEN: titleCols.en,
      providerName: provider,
      externalUrl: url.trim(),
      region: region ? [region] : [],
      cityCN: cityCols.cn,
      cityEN: cityCols.en,
      addressLine: address || null,
      ageMin: Number(ageMin),
      ageMax: Number(ageMax),
      participation,
      language,
      category,
      priceNoteCN: priceCols.cn,
      priceNoteEN: priceCols.en,
      scheduleNoteCN: type === 'PROVIDER' ? scheduleCols.cn : null,
      scheduleNoteEN: type === 'PROVIDER' ? scheduleCols.en : null,
      occurrences:
        type === 'EVENT' ? occurrences.filter((o): o is NonNullable<typeof o> => o !== null) : [],
      reason: reason.trim(),
      visited,
      displayNameMode: mode,
      ...(mode === 'custom' ? { customName: customName.trim() } : {}),
      note: note.trim() || undefined,
      ...(draftPickId ? { draftPickId } : {}),
    };
    setBusy(true);
    try {
      // A timed-out job whose abandon never landed: settle it first so it cannot
      // write a second draft, and reuse its draft if it already wrote one.
      if (pendingJobId) {
        let written: string | null;
        try {
          written = await settlePendingJob(intakeApi('parent'), pendingJobId);
        } catch {
          setError(t('picks:intake.errors.network'));
          return;
        }
        setPendingJobId(null);
        if (written) {
          setDraftPickId(written);
          payload.draftPickId = written;
        }
      }
      await picksApi.submit(payload);
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('picks:review.failed'));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setDone(false);
    setStep(1);
    // The last suggestion may have used the day's final recognition.
    intakeApi('parent')
      .enabled()
      .then(setIntakeOn)
      .catch(() => setIntakeOn(false));
    setSrcUrl('');
    setSrcFile(null);
    setSrcNote('');
    setIntakeMsg(null);
    setRecognized(false);
    setFlags({});
    setDraftPickId(null);
    setPendingJobId(null);
    setTitle('');
    setProvider('');
    setUrl('');
    setDates([{ date: '', start: '', end: '' }]);
    setSchedule('');
    setReason('');
    setNote('');
    setDuplicate(false);
  };

  /** Field label with its intake flag, if any. */
  const flagged = (text: string, key: FlagKey) => (
    <>
      {text}
      {flags[key] && (
        <FlagPill
          flag={flags[key]!}
          label={flags[key] === 'chk' ? t('picks:intake.flagCheck') : t('picks:intake.flagMissing')}
        />
      )}
    </>
  );

  return (
    <div className="bg-warm-white font-text text-[15px] leading-[1.6] text-ink">
      <Helmet>
        <title>{t('picks:submit.metaTitle')}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <section className="mx-auto max-w-[760px] px-4 pb-[50px] desk:px-7">
        <div className="pb-2 pt-[34px]">
          <div className="font-ui text-[12.5px] font-semibold uppercase tracking-[0.08em] text-primary">
            {t('picks:list.suggest')}
          </div>
          <h1 className="mt-1 font-ui text-[clamp(28px,3.2vw,40px)] font-bold leading-[1.6] text-ink [text-wrap:balance]">
            {t('picks:submit.title')}
          </h1>
          <p className="mt-2 text-ink-2">
            {intakeOn ? t('picks:intake.lede') : t('picks:submit.lede')}
          </p>
        </div>

        {intakeOn && <Steps current={done ? 3 : step === 1 ? 1 : 2} />}

        {intakeOn === null ? null : done ? (
          <div className="mt-6 rounded-[22px] border border-line bg-white px-2.5 py-[30px] text-center">
            <div className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-full bg-primary-pale text-primary">
              <Check className="h-7 w-7" aria-hidden="true" />
            </div>
            <h3 className="font-ui text-[20px] font-bold leading-[1.6]">
              {t('picks:submit.doneTitle')}
            </h3>
            <p className="mt-2 text-ink-2">{t('picks:submit.doneBody')}</p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-3.5">
              <Link to="/picks" className={btnGhost}>
                {t('picks:submit.backToPicks')}
              </Link>
              <button type="button" className={textLink} onClick={reset}>
                {t('picks:submit.another')}
              </button>
            </div>
          </div>
        ) : intakeOn && step === 1 ? (
          <div className="rounded-[22px] border border-line bg-white p-6">
            <div role="group" className="mb-4 inline-flex rounded-full bg-warm-white p-1">
              {(
                [
                  ['url', Link2, t('picks:intake.modeUrl')],
                  ['img', ImageIcon, t('picks:intake.modeImage')],
                ] as const
              ).map(([value, Icon, text]) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={srcMode === value}
                  onClick={() => setSrcMode(value)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-4 py-[7px] font-ui text-[14px] ${focusRing} ${
                    srcMode === value
                      ? 'bg-white font-semibold shadow-[0_2px_8px_-3px_rgba(60,50,30,0.4)]'
                      : ''
                  }`}
                >
                  <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
                  {text}
                </button>
              ))}
            </div>
            {srcMode === 'url' ? (
              <Field
                label={t('picks:intake.urlLabel')}
                htmlFor="s-url"
                hint={t('picks:intake.urlHint')}
              >
                <input
                  id="s-url"
                  type="url"
                  placeholder="https://"
                  className={input}
                  value={srcUrl}
                  onChange={(e) => setSrcUrl(e.target.value)}
                />
              </Field>
            ) : (
              <label
                className="block cursor-pointer rounded-[16px] border-2 border-dashed border-line bg-warm-white p-[26px] text-center text-ink-2"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const f = e.dataTransfer.files?.[0];
                  if (f) setSrcFile(f);
                }}
              >
                <ImageIcon
                  className="mx-auto mb-1.5 h-[30px] w-[30px] text-primary"
                  aria-hidden="true"
                />
                <div>
                  <b>{t('picks:intake.dropTap')}</b>
                  {t('picks:intake.dropOr')}
                </div>
                <div className="text-[12.5px] text-mute">{t('picks:intake.dropHint')}</div>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="mt-2.5 max-w-full"
                  onChange={(e) => setSrcFile(e.target.files?.[0] ?? null)}
                />
                {srcFile && <div className="mt-1.5 text-[13px] text-ink">{srcFile.name}</div>}
              </label>
            )}
            <div className="mt-3.5">
              <Field label={t('picks:intake.noteLabel')} htmlFor="s-note">
                <input
                  id="s-note"
                  maxLength={MAX_REASON}
                  placeholder={t('picks:intake.notePlaceholder')}
                  className={input}
                  value={srcNote}
                  onChange={(e) => setSrcNote(e.target.value)}
                />
              </Field>
            </div>
            {intakeMsg && <p className="mt-3 text-sm text-red-700">{intakeMsg}</p>}
            <div className="mt-4 flex flex-wrap items-center gap-3.5">
              <button type="button" className={btnPrimary} onClick={() => void recognize()}>
                <Sparkle className="h-[18px] w-[18px]" aria-hidden="true" />
                {t('picks:intake.recognize')}
              </button>
              <button
                type="button"
                className={textLink}
                onClick={() => {
                  setIntakeMsg(null);
                  setRecognized(false);
                  setFlags({});
                  // Keep what was already typed: the link and the note.
                  if (srcMode === 'url' && srcUrl.trim()) setUrl((u) => u || srcUrl.trim());
                  if (srcNote.trim()) {
                    setReason((r) => (r.trim() ? r : srcNote.trim().slice(0, MAX_REASON)));
                  }
                  setStep(3);
                }}
              >
                {t('picks:intake.skip')}
              </button>
            </div>
          </div>
        ) : intakeOn && step === 2 ? (
          <div
            role="status"
            aria-live="polite"
            className="rounded-[22px] border border-line bg-white px-5 py-[50px] text-center"
          >
            <div className="mx-auto mb-3.5 h-10 w-10 animate-spin rounded-full border-4 border-primary-pale border-t-primary motion-reduce:animate-none" />
            <b>{searching ? t('picks:intake.searchingTitle') : t('picks:intake.loadingTitle')}</b>
            <p className="mt-1.5 text-[12.5px] text-mute">
              {searching ? t('picks:intake.searchingHint') : t('picks:intake.loadingHint')}
            </p>
          </div>
        ) : (
          <div
            className={`${intakeOn ? '' : 'mt-6'} rounded-[22px] border border-line bg-white p-6`}
          >
            <h3 className="font-ui text-[19px] font-bold leading-[1.6]">
              {recognized ? t('picks:intake.checkTitle') : t('picks:submit.formTitle')}
            </h3>
            {recognized && (
              <div className="mt-2 flex flex-wrap gap-3 text-[12.5px] text-mute">
                <span className="inline-flex items-center gap-1.5">
                  <FlagPill flag="chk" label={t('picks:intake.flagCheck')} />
                  {t('picks:intake.flagCheckLegend')}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <FlagPill flag="miss" label={t('picks:intake.flagMissing')} />
                  {t('picks:intake.flagMissingLegend')}
                </span>
              </div>
            )}
            {recognized && searchSources && (
              <div className="mt-2 rounded-[12px] bg-warm-white px-3.5 py-2.5 text-[14px] text-ink-2">
                <p>{t('picks:intake.viaSearch')}</p>
                {searchSources.length > 0 && (
                  <p className="mt-1.5 text-[12.5px] text-mute">
                    {t('picks:intake.sources')}{' '}
                    {searchSources.map((u, i) => (
                      <span key={u}>
                        {i > 0 && ', '}
                        <a
                          href={u}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className={`underline ${focusRing}`}
                        >
                          {hostOf(u)}
                        </a>
                      </span>
                    ))}
                  </p>
                )}
              </div>
            )}
            {intakeMsg && (
              <p className="mt-2 rounded-[12px] bg-warm-white px-3.5 py-2.5 text-[14px] text-ink-2">
                {intakeMsg}
              </p>
            )}
            <form
              className="mt-4 grid grid-cols-1 gap-3.5 desk:grid-cols-2"
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
            >
              <Field label={flagged(t('picks:submit.type'), 'type')} full>
                <div className="flex flex-wrap gap-2">
                  {(['PROVIDER', 'EVENT'] as const).map((v) => (
                    <label key={v} className={radioCls}>
                      <input
                        type="radio"
                        name="pick-type"
                        checked={type === v}
                        onChange={() => {
                          setType(v);
                          unflag('type');
                        }}
                        className="accent-primary"
                      />
                      {v === 'PROVIDER'
                        ? t('picks:submit.typeProvider')
                        : t('picks:submit.typeEvent')}
                    </label>
                  ))}
                </div>
              </Field>
              <Field
                label={flagged(t('picks:submit.titleLabel'), 'title')}
                htmlFor="f-title"
                full
                flag={flags.title}
              >
                <input
                  id="f-title"
                  required
                  className={input}
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    unflag('title');
                  }}
                />
              </Field>
              <Field
                label={flagged(t('picks:submit.provider'), 'provider')}
                htmlFor="f-prov"
                flag={flags.provider}
              >
                <input
                  id="f-prov"
                  required
                  className={input}
                  value={provider}
                  onChange={(e) => {
                    setProvider(e.target.value);
                    unflag('provider');
                  }}
                  onBlur={checkDuplicate}
                />
              </Field>
              <Field label={flagged(t('picks:submit.url'), 'url')} htmlFor="f-url" flag={flags.url}>
                <input
                  id="f-url"
                  type="url"
                  required
                  placeholder="https://"
                  className={input}
                  value={url}
                  onChange={(e) => {
                    setUrl(e.target.value);
                    unflag('url');
                  }}
                  onBlur={checkDuplicate}
                />
              </Field>
              {duplicate && (
                <p className="rounded-[14px] border border-dashed border-pick-line bg-pick-bg px-3.5 py-2.5 text-sm text-ink-2 desk:col-span-2">
                  {t('picks:submit.duplicate')}
                </p>
              )}

              {type === 'EVENT' ? (
                <Field
                  label={flagged(t('picks:submit.dates'), 'when')}
                  full
                  hint={t('picks:submit.datesHint')}
                  flag={flags.when}
                >
                  <div className="flex flex-col gap-2">
                    {dates.map((row, i) => (
                      <div key={i} className="flex flex-wrap items-center gap-2">
                        <input
                          type="date"
                          aria-label={t('picks:submit.date')}
                          className={`${input} w-auto flex-1`}
                          value={row.date}
                          onChange={(e) =>
                            editDates((d) =>
                              d.map((r, j) => (j === i ? { ...r, date: e.target.value } : r))
                            )
                          }
                        />
                        <input
                          type="time"
                          aria-label={t('picks:submit.start')}
                          className={`${input} w-auto`}
                          value={row.start}
                          onChange={(e) =>
                            editDates((d) =>
                              d.map((r, j) => (j === i ? { ...r, start: e.target.value } : r))
                            )
                          }
                        />
                        <span className="text-mute">{t('picks:detail.to')}</span>
                        <input
                          type="time"
                          aria-label={t('picks:submit.end')}
                          className={`${input} w-auto`}
                          value={row.end}
                          onChange={(e) =>
                            editDates((d) =>
                              d.map((r, j) => (j === i ? { ...r, end: e.target.value } : r))
                            )
                          }
                        />
                        {dates.length > 1 && (
                          <button
                            type="button"
                            aria-label={t('picks:submit.removeDate')}
                            className={`grid h-9 w-9 place-items-center rounded-full text-mute hover:bg-warm-white ${focusRing}`}
                            onClick={() => setDates((d) => d.filter((_, j) => j !== i))}
                          >
                            <X className="h-4 w-4" aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    ))}
                    {dates.length < 10 && (
                      <button
                        type="button"
                        className={`${textLink} self-start`}
                        onClick={() => setDates((d) => [...d, { date: '', start: '', end: '' }])}
                      >
                        <Plus className="h-4 w-4" aria-hidden="true" />
                        {t('picks:submit.addDate')}
                      </button>
                    )}
                  </div>
                </Field>
              ) : (
                <Field
                  label={flagged(t('picks:submit.schedule'), 'when')}
                  htmlFor="f-sched"
                  full
                  flag={flags.when}
                >
                  <input
                    id="f-sched"
                    required
                    placeholder={t('picks:submit.schedulePlaceholder')}
                    className={input}
                    value={schedule}
                    onChange={(e) => {
                      setSchedule(e.target.value);
                      unflag('when');
                    }}
                  />
                </Field>
              )}

              <Field
                label={flagged(t('picks:submit.address'), 'address')}
                htmlFor="f-addr"
                hint={t('picks:submit.optional')}
                flag={flags.address}
              >
                <input
                  id="f-addr"
                  className={input}
                  value={address}
                  onChange={(e) => {
                    setAddress(e.target.value);
                    unflag('address');
                  }}
                />
              </Field>
              <Field
                label={flagged(t('picks:submit.city'), 'city')}
                htmlFor="f-city"
                flag={flags.city}
              >
                <input
                  id="f-city"
                  className={input}
                  value={city}
                  onChange={(e) => {
                    setCity(e.target.value);
                    unflag('city');
                  }}
                />
              </Field>
              <Field
                label={flagged(t('picks:detail.place'), 'region')}
                htmlFor="f-region"
                flag={flags.region}
              >
                <select
                  id="f-region"
                  required
                  className={input}
                  value={region}
                  onChange={(e) => {
                    setRegion(e.target.value);
                    unflag('region');
                  }}
                >
                  <option value="">{t('picks:submit.choose')}</option>
                  {REGIONS.map((r) => (
                    <option key={r} value={r}>
                      {t(`common:region.${r}`)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={flagged(t('picks:detail.age'), 'age')} flag={flags.age}>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    max={99}
                    step={0.5}
                    required
                    aria-label={t('picks:submit.ageMin')}
                    className={input}
                    value={ageMin}
                    onChange={(e) => {
                      setAgeMin(e.target.value);
                      unflag('age');
                    }}
                  />
                  <span className="text-mute">{t('picks:detail.to')}</span>
                  <input
                    type="number"
                    min={0}
                    max={99}
                    step={0.5}
                    required
                    aria-label={t('picks:submit.ageMax')}
                    className={input}
                    value={ageMax}
                    onChange={(e) => {
                      setAgeMax(e.target.value);
                      unflag('age');
                    }}
                  />
                </div>
              </Field>
              <Field
                label={flagged(t('picks:detail.participation'), 'participation')}
                htmlFor="f-part"
                flag={flags.participation}
              >
                <select
                  id="f-part"
                  className={input}
                  value={participation}
                  onChange={(e) => {
                    setParticipation(e.target.value);
                    unflag('participation');
                  }}
                >
                  {PARTICIPATIONS.map((p) => (
                    <option key={p} value={p}>
                      {t(`common:participation.${p}`)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label={flagged(t('picks:submit.language'), 'language')}
                htmlFor="f-lang"
                flag={flags.language}
              >
                <select
                  id="f-lang"
                  required
                  className={input}
                  value={language}
                  onChange={(e) => {
                    setLanguage(e.target.value);
                    unflag('language');
                  }}
                >
                  <option value="">{t('picks:submit.choose')}</option>
                  {LANGUAGES.map((l) => (
                    <option key={l} value={l}>
                      {l === 'NA'
                        ? t('picks:submit.languageNA')
                        : t(`common:teachingLanguage.${l}`)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label={flagged(t('picks:submit.category'), 'category')}
                htmlFor="f-cat"
                flag={flags.category}
              >
                <select
                  id="f-cat"
                  required
                  className={input}
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value);
                    unflag('category');
                  }}
                >
                  <option value="">{t('picks:submit.choose')}</option>
                  {ACTIVITY_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {t(`common:category.${c.value}`)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label={flagged(t('picks:detail.price'), 'price')}
                htmlFor="f-price"
                full
                hint={t('picks:submit.optional')}
                flag={flags.price}
              >
                <input
                  id="f-price"
                  className={input}
                  value={price}
                  onChange={(e) => {
                    setPrice(e.target.value);
                    unflag('price');
                  }}
                />
              </Field>
              <Field
                label={
                  <>
                    {t('picks:detail.why')}
                    <span className="font-normal text-mute">{t('picks:submit.reasonHint')}</span>
                  </>
                }
                htmlFor="f-why"
                full
                hint={
                  <>
                    {reasonLength} / {MAX_REASON}
                    <br />
                    {t('picks:review.piiHint')}
                  </>
                }
              >
                <textarea
                  id="f-why"
                  required
                  maxLength={MAX_REASON}
                  placeholder={t('picks:submit.reasonPlaceholder')}
                  className={`${input} min-h-[110px] resize-y leading-[1.6]`}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </Field>
              <div className="desk:col-span-2">
                <label className={radioCls}>
                  <input
                    type="checkbox"
                    checked={visited}
                    onChange={(e) => setVisited(e.target.checked)}
                    className="accent-primary"
                  />
                  {t(`picks:visited.${type}`)}
                </label>
              </div>
              <Field label={t('picks:review.byline')} full>
                <div className="flex flex-wrap gap-2">
                  {(
                    [
                      ['account', t('review:editor.nameAccount')],
                      ['custom', t('review:editor.nameCustom')],
                      ['anonymous', t('review:editor.nameAnonymous')],
                    ] as const
                  ).map(([value, label]) => (
                    <label key={value} className={radioCls}>
                      <input
                        type="radio"
                        name="pick-byline"
                        checked={mode === value}
                        onChange={() => setMode(value)}
                        className="accent-primary"
                      />
                      {label}
                    </label>
                  ))}
                </div>
                {mode === 'custom' && (
                  <input
                    required
                    maxLength={40}
                    placeholder={t('review:editor.customNamePlaceholder')}
                    className={input}
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                  />
                )}
              </Field>
              <Field label={t('picks:submit.note')} htmlFor="f-note" full>
                <input
                  id="f-note"
                  className={input}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </Field>
              {error && <p className="text-sm text-red-700 desk:col-span-2">{error}</p>}
              <div className="mt-2 flex flex-wrap items-center gap-3.5 desk:col-span-2">
                <button type="submit" className={btnPrimary} disabled={busy}>
                  {t('picks:submit.submit')}
                </button>
                {intakeOn && (
                  <button
                    type="button"
                    className={textLink}
                    onClick={() => {
                      setIntakeMsg(null);
                      setStep(1);
                    }}
                  >
                    {t('picks:intake.again')}
                  </button>
                )}
              </div>
            </form>
          </div>
        )}
      </section>
    </div>
  );
}

/** Prototype `.steps`; spacing separates the steps (no arrow glyphs). */
function Steps({ current }: { current: 1 | 2 | 3 }) {
  const { t } = useTranslation('picks');
  const labels = [t('intake.stepInput'), t('intake.stepCheck'), t('intake.stepSubmit')];
  return (
    <ol className="mb-3.5 mt-[26px] flex flex-wrap items-center gap-x-5 gap-y-2 font-ui text-[13px] text-mute">
      {labels.map((label, i) => {
        const n = (i + 1) as 1 | 2 | 3;
        const state = n < current ? 'done' : n === current ? 'on' : 'todo';
        return (
          <li
            key={label}
            aria-current={state === 'on' ? 'step' : undefined}
            className={`inline-flex items-center gap-1.5 ${state === 'on' ? 'font-semibold text-ink' : ''}`}
          >
            <span
              className={`grid h-[22px] w-[22px] place-items-center rounded-full border-[1.5px] text-[12px] ${
                state === 'on'
                  ? 'border-primary bg-primary text-white'
                  : state === 'done'
                    ? 'border-primary-pale bg-primary-pale text-primary-dark'
                    : 'border-line'
              }`}
            >
              {state === 'done' ? <Check className="h-3 w-3" aria-hidden="true" /> : n}
            </span>
            {label}
          </li>
        );
      })}
    </ol>
  );
}
